<template>
  <div id="printers">
    <PageTitle :title="$t('printers.title')" :subtitle="$t('printers.description')">
      <button class="btn btn-primary" @click="newPrinter">
        {{$t('printers.new')}}
      </button>
    </PageTitle>

    <PageContainer>

      <Empty v-if="printers.length === 0" :text="$t('printers.empty')" :button="{text: $t('printers.new'), action: newPrinter}"></Empty>

      <table class="table" v-if="printers.length > 0">
        <tr>
          <th class="icon"></th>
          <th class="name">{{$t('printers.name')}}</th>
          <th class="ip">{{$t('printers.ip_address')}}</th>
          <th class="printing-group">{{$t('printers.group')}}</th>
          <th class="action"></th>
        </tr>
        <tr v-for="(printer, printerIndex) of printers" :key="'printer-' + printerIndex">
          <td class="icon">
            <div class="table__icon">
              <svgicon src="icon/printer.svg" :height="16" :width="16"></svgicon>
            </div>
          </td>
          <td class="name">{{printer.name}}</td>
          <td class="ip">{{printer.ip}}</td>
          <td class="printing-group">{{getGroupName(printer.group)}}</td>
          <td class="menu-items">
            <div class="actions">
              <ActionMenu icon="/icon/actions-horizontal.png">
                <div class="item" @click="editPrinter(printer)">{{$t('edit')}}</div>
                <div class="item" @click="removePrinter(printerIndex)">{{$t('remove')}}</div>
              </ActionMenu>
            </div>
          </td>
        </tr>
      </table>

    </PageContainer>

    <Modal name="printer-edit" :title="$t('printers.edit')" :subtitle="$t('printers.edit_subtitle')"
           :actions="[{name: $t('back'), action: 'close'}, {name: $t('save'), action: savePrinter, primary: true}]">
      <template v-if="printer">
        <div class="group">
          <label>{{$t('printers.name')}}</label>
          <input type="text" v-model="printer.name">
        </div>
        <div class="group">
          <label>{{$t('printers.ip_address')}}</label>
          <input type="text" v-model="printer.ip" placeholder="000.000.000.000">
        </div>
        <div class="group">
          <label>{{$t('printers.group')}}</label>
          <select v-model="printer.group">
            <option :value="null">{{$t('none')}}</option>
            <option :value="group.id" v-for="group of printerGroups" :key="'group' + group.id">{{$to(group.name)}}</option>
          </select>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
  import ActionMenu from "@/components/tools/ActionMenu";
  import Empty from "@/components/tools/Empty";
  import PageContainer from "@/components/PageContainer";
  import PageTitle from "@/components/PageTitle";
  import {Core} from "@/core/Core";

  export default {
    name: "printers",

    components: { PageContainer, PageTitle, ActionMenu, Empty },

    data() {
      return {
        company: null,
        printer: null,
        printers: [],
        printerGroups: []
      }
    },

    async mounted() {
      showSpinner();
      this.$store.state.pageTitleTranslationKey = "printers.title";
      this.$store.state.pageIcon = "/icon/printer.svg";
      this.company = await this.$store.getters.getContextCompany;
      await this.loadPrinters();
      hideSpinner();
    },

    methods: {
      async loadPrinters() {
        try {
          let [printers, printerGroups] = await Promise.all([
              Core.request("GET", "/api/pos_data_storage/global_printer?company=" + this.company.id),
              Core.request("GET", "/api/printer_groups?company=" + this.company.id)
          ]);
          if (printers) {
            this.printers = printers.data && printers.data.printers ? printers.data.printers : [];
          }
          if (printerGroups) {
            this.printerGroups = printerGroups.data;
            this.printerGroups.push({
              name: {fr: "Tous les produits", en: "All products"},
              id: "all"
            });
          }
        } catch (e) {
          console.log(e);
        }
      },
      getGroupName(id) {
        let group = this.printerGroups.find(g => g.id === id);
        if (group) {
          return this.$to(group.name);
        }
        return this.$t('none');
      },
      editPrinter(printer) {
        this.printer = printer;
        this.$modal.show("printer-edit");
      },
      newPrinter() {
        this.printer = {
          group: "all",
          ip: "",
          name: ""
        }
        this.$modal.show("printer-edit");
      },

      async removePrinter(index) {
        let response = await showConfirmation(this.$t('warning'), this.$t('printers.delete'));
        if (response) {
          let data = {
            printers: [...this.printers]
          };
          data.printers.splice(index, 1);
          await this.savePrinterData(data);
        }
      },

      async savePrinter() {
        if (!this.printer.name || !this.printer.ip) {
          toast(this.$t("printers.incomplete"), "warning", this.$t("warning"));
          return;
        }
        let data = {
          printers: [...this.printers]
        }
        let exists = this.printers.find(p => p === this.printer);
        if (!exists) {
          data.printers.push(this.printer);
        }
        await this.savePrinterData(data);
      },
      async savePrinterData(data) {
        try {
          showSpinner();
          let response = await Core.request("PUT", "/api/pos_data_storage/global_printer?company=" + this.company.id, {
            data: data
          });
          hideSpinner();
          if (response) {
            this.printers = data.printers;
            this.$modal.show("printer-edit", false);
            toast(this.$t('printers.save_success'), "success", this.$t("success"));
          }
        } catch (e) {
          hideSpinner();
          console.log(e);
          toast(this.$t("printers.save_error"), "error", this.$t("error.title"));
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  #printers {
    display: flex;
    flex-direction: column;

    .name {
      font-weight: 600;
    }

    .table {
      width: 100%;

      tr {
        &:not(:last-child) {
          td, th {
            border-bottom: 1px solid #e6e6e6;
          }
        }
      }

      td, th {
        &.icon {
          width: 1px;
        }
        &.printing-group, &.ip, &.name {
          width: 30%;
        }
      }

      th {
        text-align: left;
        font-size: 14px;
        padding-bottom: 16px;
      }

      td {
        padding-top: 16px;
        padding-bottom: 16px;
        &:first-child {
          padding-left: 16px;
        }
        &:last-child {
          padding-right: 16px;
        }
      }
    }
    .actions {
      height: 32px;
      width: 100%;
    }
    .table__icon {
      height: 32px;
      width: 32px;
      display: flex;
      border-radius: 6px;
      background-color: #f5f5f5;
      align-items: center;
      justify-content: center;
      fill: #ccc;
      margin-right: 16px;
    }
  }

  @media screen and (max-width: 500px) {
    td, th {
      &.icon {
        display: none;
      }
    }
  }
</style>